// components/Dropdown.js
import { useRef } from 'react'
import { BiChevronDown, BiChevronUp } from 'react-icons/bi'
import { ClipLoader } from 'react-spinners'
import { motion, AnimatePresence } from 'framer-motion'
import useClickOutside from '../../core/hooks/useClickOutside'
import { createPortal } from 'react-dom'


const Dropdown = ({
    open,
    setOpen,
    options = [],
    renderOption,
    onSelect,
    isLoading,
    isError,
    onRetry,
    placeholder,
    searchValue,
    onSearchChange,
    label,
    className = '',
    dropdownClass = '',
    togglerContent,
    noDataText = 'No results found',
    errorText = 'Unknown error',
    position = 'bottom',
    align = 'start',
    offset = 8,
}) => {
    const ref = useRef()
    const togglerRef = useRef()
    useClickOutside(ref, togglerRef, () => setOpen(false))

    const getPositionStyles = () => {
        const baseStyles = {
            position: 'absolute',
            zIndex: 1002,
            width: 'auto',
            maxHeight: '300px',
            overflowY: 'auto',
        }

        const offsetPx = `${offset}px`
        switch (position) {
            case 'top':
                return { ...baseStyles, bottom: `calc(100% + ${offsetPx})`, left: 0, right: 0, width: '100%' }
            case 'left':
                return { ...baseStyles, right: `calc(100% + ${offsetPx})`, top: getVerticalAlignment(align), width: '100%', minWidth: '100%' }
            case 'right':
                return { ...baseStyles, left: `calc(100% + ${offsetPx})`, top: getVerticalAlignment(align), width: '100%', minWidth: '100%' }
            case 'bottom':
            default:
                return { ...baseStyles, top: `calc(100% + ${offsetPx})`, left: 0, right: 0, width: '100%' }
        }
    }

    const getVerticalAlignment = (alignment) => {
        switch (alignment) {
            case 'center': return '50%'
            case 'end': return '100%'
            default: return '0'
        }
    }

    const getTransform = () => {
        if (position === 'left' || position === 'right') {
            switch (align) {
                case 'center': return 'translateY(-50%)'
                case 'end': return 'translateY(-100%)'
                default: return ''
            }
        }
        return ''
    }

    return (
        <div ref={ref} className={`relative text-sm w-full max-w-lg  ${className}`}>
            {/* Toggler */}
            <div
                onClick={() => setOpen(prev => !prev)}
                className="relative rounded-lg border h-[40px] bg-[#59168B4D] border-[#AD46FF4D] px-3 py-2 flex items-center gap-2 cursor-pointer"
            >
                {label && <span className="absolute left-0 -top-8 text-lg">{label}</span>}
                {togglerContent}
                <input
                    ref={togglerRef}
                    className="w-full bg-transparent outline-none"
                    placeholder={placeholder}
                    value={searchValue}
                    onChange={e => onSearchChange?.(e.target.value)}
                />
                {open
                    ? <BiChevronUp color='#A78BFA' className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2" size={20} />
                    : <BiChevronDown color='#A78BFA' className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2" size={20} />
                }
            </div>

            {/* Dropdown List with AnimatePresence */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        style={{
                            ...getPositionStyles(),
                            transform: getTransform(),
                        }}
                        className={`bg-white dark:bg-cardBgDark rounded-lg border border-main border-opacity-20 ${dropdownClass}`}
                        initial={{ opacity: 0, scale: 0.95, y: position === 'bottom' ? -5 : 5 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: position === 'bottom' ? -5 : 5 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30, duration: 0.2 }}
                    >
                        {isLoading ? (
                            <div className="p-4 text-center">
                                <ClipLoader size={18} color="#FC6432" />
                            </div>
                        ) : options.length > 0 ? (
                            options.map((option, idx) => (
                                <div
                                    key={idx}
                                    className="px-5 py-3 hover:opacity-70 cursor-pointer flex items-center gap-2"
                                    onClick={() => onSelect(option)}
                                >
                                    {renderOption(option)}
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center flex flex-col gap-2 items-center">
                                {isError ? (
                                    <>
                                        {errorText}
                                        {onRetry && (
                                            <button onClick={onRetry} className="p-1 text-active">
                                                Retry
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <>{noDataText}</>
                                )}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default Dropdown
